import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Loader, Image, X, Plus, Check, Link } from 'lucide-react';
import { convertConversationToRecipe, sendChatMessage, analyzeImages, saveRecipe, extractSingleRecipe } from '../utils/recipeHelpers';
import { chats as chatsApi, ai, recipes as recipesApi, user as userApi } from '../utils/api';
import FoodPreferences from './FoodPreferences';
import BottomNav from './BottomNav';
import ReactMarkdown from 'react-markdown';
import { recipeMarkdownComponents, chatMarkdownComponents } from './MarkdownRenderers';
import { 
  trackChatSessionStarted, 
  trackChatMessageSent, 
  trackRecipeCreatedChat 
} from '../utils/analytics';

// Fun loading phrases - bee and cooking themed
const loadingPhrases = [
  "Thinking...",
  "Marinating...",
  "Tasting the recipe...",
];

// Hook for rotating loading phrases every 3 seconds
const useRotatingPhrase = (isActive) => {
  const [phrase, setPhrase] = useState(loadingPhrases[0]);
  const indexRef = useRef(0);
  
  useEffect(() => {
    if (!isActive) {
      indexRef.current = 0;
      setPhrase(loadingPhrases[0]);
      return;
    }
    
    // Set initial random phrase
    indexRef.current = Math.floor(Math.random() * loadingPhrases.length);
    setPhrase(loadingPhrases[indexRef.current]);
    
    // Rotate every 3 seconds
    const interval = setInterval(() => {
      indexRef.current = (indexRef.current + 1) % loadingPhrases.length;
      setPhrase(loadingPhrases[indexRef.current]);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [isActive]);
  
  return phrase;
};

// Food-specific placeholder images organized by category
const FOOD_IMAGE_CATEGORIES = {
  chicken: 'https://images.unsplash.com/photo-1598103442097-8b74394b95c6?w=300&h=200&fit=crop',
  beef: 'https://images.unsplash.com/photo-1546833999-b9f581a1996d?w=300&h=200&fit=crop',
  pork: 'https://images.unsplash.com/photo-1432139555190-58524dae6a55?w=300&h=200&fit=crop',
  fish: 'https://images.unsplash.com/photo-1467003909585-2f8a72700288?w=300&h=200&fit=crop',
  salmon: 'https://images.unsplash.com/photo-1467003909585-2f8a72700288?w=300&h=200&fit=crop',
  shrimp: 'https://images.unsplash.com/photo-1565680018434-b513d5e5fd47?w=300&h=200&fit=crop',
  pasta: 'https://images.unsplash.com/photo-1473093295043-cdd812d0e601?w=300&h=200&fit=crop',
  pizza: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=300&h=200&fit=crop',
  salad: 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=300&h=200&fit=crop',
  soup: 'https://images.unsplash.com/photo-1547592166-23ac45744acd?w=300&h=200&fit=crop',
  sandwich: 'https://images.unsplash.com/photo-1528735602780-2552fd46c7af?w=300&h=200&fit=crop',
  burger: 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=300&h=200&fit=crop',
  taco: 'https://images.unsplash.com/photo-1565299585323-38d6b0865b47?w=300&h=200&fit=crop',
  rice: 'https://images.unsplash.com/photo-1516684732162-798a0062be99?w=300&h=200&fit=crop',
  curry: 'https://images.unsplash.com/photo-1455619452474-d2be8b1e70cd?w=300&h=200&fit=crop',
  stir: 'https://images.unsplash.com/photo-1512058564366-18510be2db19?w=300&h=200&fit=crop',
  cake: 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=300&h=200&fit=crop',
  cookie: 'https://images.unsplash.com/photo-1499636136210-6f4ee915583e?w=300&h=200&fit=crop',
  bread: 'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=300&h=200&fit=crop',
  waffle: 'https://images.unsplash.com/photo-1562376552-0d160a2f238d?w=300&h=200&fit=crop',
  pancake: 'https://images.unsplash.com/photo-1567620905732-2d1ec7ab7445?w=300&h=200&fit=crop',
  french: 'https://images.unsplash.com/photo-1484723091739-30a097e8f929?w=300&h=200&fit=crop', // french toast
  oatmeal: 'https://images.unsplash.com/photo-1517673400267-0251440c45dc?w=300&h=200&fit=crop',
  egg: 'https://images.unsplash.com/photo-1525351484163-7529414344d8?w=300&h=200&fit=crop',
  breakfast: 'https://images.unsplash.com/photo-1533089860892-a7c6f0a88666?w=300&h=200&fit=crop',
  vegetable: 'https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?w=300&h=200&fit=crop',
  cauliflower: 'https://images.unsplash.com/photo-1510627498534-cf7e9002facc?w=300&h=200&fit=crop',
  broccoli: 'https://images.unsplash.com/photo-1459411552884-841db9b3cc2a?w=300&h=200&fit=crop',
  potato: 'https://images.unsplash.com/photo-1518977676601-b53f82aba655?w=300&h=200&fit=crop',
  smoothie: 'https://images.unsplash.com/photo-1505252585461-04db1eb84625?w=300&h=200&fit=crop',
  dessert: 'https://images.unsplash.com/photo-1488477181946-6428a0291777?w=300&h=200&fit=crop',
  muffin: 'https://images.unsplash.com/photo-1607958996333-41aef7caefaa?w=300&h=200&fit=crop',
  toast: 'https://images.unsplash.com/photo-1484723091739-30a097e8f929?w=300&h=200&fit=crop',
  banana: 'https://images.unsplash.com/photo-1558401391-7899b4bd5bbf?w=300&h=200&fit=crop', // banana bread
  apple: 'https://images.unsplash.com/photo-1568571780765-9276ac8b75a2?w=300&h=200&fit=crop', // apple pie/dessert
  chocolate: 'https://images.unsplash.com/photo-1606313564200-e75d5e30476c?w=300&h=200&fit=crop',
  lemon: 'https://images.unsplash.com/photo-1519915028121-7d3463d20b13?w=300&h=200&fit=crop',
  berry: 'https://images.unsplash.com/photo-1488477181946-6428a0291777?w=300&h=200&fit=crop',
  default: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=300&h=200&fit=crop',
};

// Get a food image based on recipe name keywords
const getFoodImage = (recipeName) => {
  if (!recipeName) return FOOD_IMAGE_CATEGORIES.default;
  
  const lowerName = recipeName.toLowerCase();
  
  // Check for category matches in order of specificity
  for (const [category, url] of Object.entries(FOOD_IMAGE_CATEGORIES)) {
    if (category !== 'default' && lowerName.includes(category)) {
      return url;
    }
  }
  
  // Check for related keywords
  if (lowerName.includes('wing') || lowerName.includes('thigh') || lowerName.includes('drumstick')) {
    return FOOD_IMAGE_CATEGORIES.chicken;
  }
  if (lowerName.includes('steak') || lowerName.includes('roast')) {
    return FOOD_IMAGE_CATEGORIES.beef;
  }
  if (lowerName.includes('noodle') || lowerName.includes('spaghetti') || lowerName.includes('linguine')) {
    return FOOD_IMAGE_CATEGORIES.pasta;
  }
  if (lowerName.includes('wrap') || lowerName.includes('burrito')) {
    return FOOD_IMAGE_CATEGORIES.taco;
  }
  if (lowerName.includes('fry') || lowerName.includes('fried')) {
    return FOOD_IMAGE_CATEGORIES.stir;
  }
  if (lowerName.includes('bake') || lowerName.includes('roast')) {
    return FOOD_IMAGE_CATEGORIES.vegetable;
  }
  
  return FOOD_IMAGE_CATEGORIES.default;
};

// Helper to check if a message contains a recipe
const messageHasRecipe = (content) => {
  if (!content) return false;
  const lower = content.toLowerCase();
  const hasIngredients = lower.includes('ingredients');
  const hasInstructions = lower.includes('instructions') || lower.includes('directions') || /\b(step\s*1|1\..*preheat|1\..*combine)/i.test(content);
  return hasIngredients && hasInstructions;
};

// Helper to split multi-recipe content into individual recipe sections
const splitIntoRecipes = (content) => {
  if (!content) return [];
  
  const recipeNames = extractAllRecipeNames(content);
  if (recipeNames.length <= 1) {
    // Single or no recipe - return as-is
    return [{ name: recipeNames[0] || null, content }];
  }
  
  // Split content by finding recipe headers that are on their own line
  const recipes = [];
  const lines = content.split('\n');
  
  for (let i = 0; i < recipeNames.length; i++) {
    const currentName = recipeNames[i];
    const nextName = recipeNames[i + 1];
    
    // Find the line index where this recipe header appears (on its own line)
    let startLineIdx = -1;
    for (let j = 0; j < lines.length; j++) {
      const line = lines[j].trim();
      if (line === `**${currentName}**`) {
        // Check if next non-empty line is a description (not "- description" which would be a list item)
        let k = j + 1;
        while (k < lines.length && lines[k].trim() === '') k++;
        if (k < lines.length) {
          const nextLine = lines[k].trim();
          // Must be followed by description, not a list item continuation
          if (!nextLine.startsWith('-') && !nextLine.startsWith('*-')) {
            startLineIdx = j;
            break;
          }
        }
      }
    }
    
    if (startLineIdx === -1) continue;
    
    // Find end line - either next recipe header or Tips section or end of content
    let endLineIdx = lines.length;
    
    if (nextName) {
      for (let j = startLineIdx + 1; j < lines.length; j++) {
        if (lines[j].trim() === `**${nextName}**`) {
          // Check it's the recipe header, not inline mention
          let k = j + 1;
          while (k < lines.length && lines[k].trim() === '') k++;
          if (k < lines.length && !lines[k].trim().startsWith('-')) {
            endLineIdx = j;
            break;
          }
        }
      }
    }
    
    // Also check for shared "Tips & Tricks" section
    for (let j = startLineIdx + 1; j < endLineIdx; j++) {
      const line = lines[j].trim().toLowerCase();
      if (line.includes('tips & tricks for both') || line.includes('tips for both')) {
        endLineIdx = j;
        break;
      }
    }
    
    const recipeContent = lines.slice(startLineIdx, endLineIdx).join('\n').trim();
    if (recipeContent) {
      recipes.push({ name: currentName, content: recipeContent });
    }
  }
  
  return recipes.length > 0 ? recipes : [{ name: null, content }];
};

// Helper to extract ALL recipe names from content (for multiple recipes)
const extractAllRecipeNames = (content) => {
  if (!content) return [];
  
  // Normalize malformed bold markers - fix **text\n** to **text**
  const normalizedContent = content.replace(/\*\*([^*\n]+)\n\*\*/g, '**$1**');
  
  const recipes = [];
  
  // Skip these words - they are section headers, not recipe names
  const skipWords = ['ingredients', 'instructions', 'directions', 'tips', 'tricks', 'notes', 'step', 'serving', 'storage', 'variations', 'preparation', 'practical', 'flavor', 'finishing', 'cook', 'prep', 'no cook', 'nutrition', 'for the', 'topping', 'mixture', 'sauce', 'dressing', 'marinade'];
  
  const lines = normalizedContent.split('\n');
  
  // STRATEGY 1: Look for bold headers on their own line (preferred format)
  let i = 0;
  while (i < lines.length) {
    const line = lines[i].trim();
    // Check if line is ONLY a bold header like **Recipe Name** (nothing else on the line)
    const boldMatch = line.match(/^\*\*([^*]+)\*\*$/);
    if (boldMatch) {
      const name = boldMatch[1].trim();
      const nameLower = name.toLowerCase();
      
      // Skip if it's a section header or ends with colon
      const isSkipWord = skipWords.some(word => nameLower.startsWith(word) || nameLower === word);
      const endsWithColon = name.endsWith(':');
      // Only skip very short single words that are likely ingredients (4 chars or less)
      const isTooShort = name.split(' ').length === 1 && name.length <= 4;
      
      if (!isSkipWord && !endsWithColon && !isTooShort) {
        // Check if next non-empty line looks like a recipe description
        let j = i + 1;
        while (j < lines.length && lines[j].trim() === '') j++;
        
        if (j < lines.length) {
          const nextLine = lines[j].trim();
          // Recipe descriptions typically start with a capital letter and are prose text
          // NOT: dashes (ingredients), numbers (steps), or section headers like "Servings:"
          const isDescription = (
            /^[A-Z*]/.test(nextLine) &&  // Starts with capital letter or italic marker
            !nextLine.startsWith('-') && 
            !nextLine.startsWith('*-') &&  // bullet list
            !nextLine.match(/^\d+[\.\)]/) &&  // numbered list
            !nextLine.match(/^(Servings|Prep|Cook|Time|Yield|Makes|Serves):/i) &&  // section headers
            !nextLine.match(/^\*\*[^*]+\*\*$/)  // not another bold header
          );
          
          if (isDescription && !recipes.includes(name)) {
            recipes.push(name);
          }
        }
      }
    }
    i++;
  }
  
  // STRATEGY 2: If no recipes found via bold headers, try to find recipe name from intro sentence
  // e.g., "Here's a recipe for **Rachel's Sweet Potato Casserole**" or just inline mention
  if (recipes.length === 0 && messageHasRecipe(content)) {
    // Look for pattern: "recipe for **Name**" or "called **Name**" in first few lines
    const firstParagraph = lines.slice(0, 5).join(' ');
    const introMatch = firstParagraph.match(/(?:recipe\s+(?:for|called)|here'?s?\s+(?:a|the|my|your)?\s*[^*]*?)\*\*([^*]+)\*\*/i);
    if (introMatch) {
      const name = introMatch[1].trim();
      const nameLower = name.toLowerCase();
      const isSkipWord = skipWords.some(word => nameLower.startsWith(word) || nameLower === word);
      if (!isSkipWord && name.length > 4 && !recipes.includes(name)) {
        recipes.push(name);
      }
    }
    
    // STRATEGY 3: Find ANY bold text that looks like a recipe title (capitalized, reasonable length)
    // This handles cases where the title isn't in a standard format
    if (recipes.length === 0) {
      const allBoldMatches = content.match(/\*\*([^*]+)\*\*/g) || [];
      for (const match of allBoldMatches) {
        const name = match.replace(/\*\*/g, '').trim();
        const nameLower = name.toLowerCase();
        
        // Must be a proper title: 3+ words, starts with capital, not a section header
        const isSkipWord = skipWords.some(word => nameLower.startsWith(word) || nameLower === word);
        const endsWithColon = name.endsWith(':');
        const wordCount = name.split(/\s+/).length;
        const startsWithCapital = /^[A-Z]/.test(name);
        
        if (!isSkipWord && !endsWithColon && wordCount >= 2 && wordCount <= 10 && startsWithCapital && !recipes.includes(name)) {
          // Check it's not an ingredient (ingredients have quantities before them)
          const context = content.substring(Math.max(0, content.indexOf(match) - 20), content.indexOf(match));
          const hasQuantityBefore = /\d+\s*$/.test(context) || /[-•]\s*$/.test(context);
          if (!hasQuantityBefore) {
            recipes.push(name);
            break; // Only take the first good match
          }
        }
      }
    }
  }
  
  return recipes;
};

// Helper to extract single recipe name (for backwards compatibility)
const extractRecipeName = (content) => {
  const names = extractAllRecipeNames(content);
  return names.length > 0 ? names[0] : null;
};

const ChatRecipeCreator = ({ onBack, recipeId = null, recipeTitle = null }) => {
  const navigate = useNavigate();
  
  // For recipe-specific chats, don't use localStorage - load from API
  // For general chats (no recipeId), use localStorage as before
  const [currentChatId, setCurrentChatId] = useState(() => {
    if (recipeId) return null; // Will be loaded from API
    return localStorage.getItem('currentChatId') || null;
  });
  const [chatLoading, setChatLoading] = useState(true);
  
  // Initial welcome message - customize based on context
  const getWelcomeMessage = () => {
    if (recipeId && recipeTitle) {
      return `Hi! I'm here to help with **${recipeTitle}**. Ask me anything about this recipe - cooking tips, substitutions, scaling, or modifications!`;
    }
    return "Hi! I'm your recipe assistant. Tell me what you'd like to cook, or upload an image of a recipe. You can describe ingredients you have, a cuisine you're craving, or just what sounds good!";
  };
  
  // Initialize messages - will be loaded from API if chatId exists
  const [messages, setMessages] = useState([{
    role: 'assistant',
    content: getWelcomeMessage(),
  }]);
  
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingImages, setPendingImages] = useState([]);
  const [showImageTypeModal, setShowImageTypeModal] = useState(false);
  const [selectedImageType, setSelectedImageType] = useState(null);
  const [foodPreferences, setFoodPreferences] = useState(null);
  const [measurementSystem, setMeasurementSystem] = useState(null);
  
  // Load user's measurement system preference
  useEffect(() => {
    const loadMeasurement = async () => {
      try {
        const res = await userApi.getMeasurementSystem();
        if (res.data?.measurement_system) {
          setMeasurementSystem(res.data.measurement_system);
        }
      } catch (e) {
        // Default to imperial if load fails
      }
    };
    loadMeasurement();
  }, []);
  const [pendingChoices, setPendingChoices] = useState({});
  // Track saved recipes with their IDs: { "Recipe Name": "recipe_id_123", ... }
  const [savedRecipes, setSavedRecipes] = useState({});
  const [savingRecipeName, setSavingRecipeName] = useState(null);
  const [chatTitle, setChatTitle] = useState(recipeTitle ? `Chat: ${recipeTitle}` : null); // Track chat title for display
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editingTitleValue, setEditingTitleValue] = useState('');
  const [showUrlModal, setShowUrlModal] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [importingUrl, setImportingUrl] = useState(false);
  // Track source images for imported recipes (keyed by source URL)
  const [importedSourceImages, setImportedSourceImages] = useState({});
  // First-time tooltip for Add to Recipe Box button
  const [showAddToBoxTip, setShowAddToBoxTip] = useState(() => {
    const hasSeenTip = typeof window !== 'undefined' && localStorage.getItem('hasSeenAddToBoxTip') === 'true';
    return !hasSeenTip;
  });
  // Food preferences: open by default on first visit, remember when closed
  const [preferencesExpanded, setPreferencesExpanded] = useState(() => {
    // Check localStorage during initial state - return true if never closed before
    // Using a versioned key to ensure fresh start
    const hasClosedBefore = typeof window !== 'undefined' && localStorage.getItem('foodPrefsClosedV2') === 'true';
    return !hasClosedBefore;
  });
  
  // Rotating loading phrases for chat and saving
  const loadingPhrase = useRotatingPhrase(loading || savingRecipeName !== null);
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const titleInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Title editing handlers
  const handleStartEditTitle = () => {
    if (!currentChatId) return; // Can't edit title for unsaved chats
    setEditingTitleValue(chatTitle || 'New Recipe Chat');
    setIsEditingTitle(true);
    setTimeout(() => titleInputRef.current?.focus(), 50);
  };

  const handleSaveTitle = async () => {
    if (!editingTitleValue.trim() || !currentChatId) {
      setIsEditingTitle(false);
      return;
    }
    
    try {
      await chatsApi.update(currentChatId, { title: editingTitleValue.trim() });
      setChatTitle(editingTitleValue.trim());
      setIsEditingTitle(false);
    } catch (error) {
      console.error('Failed to update title:', error);
    }
  };

  const handleCancelEditTitle = () => {
    setIsEditingTitle(false);
    setEditingTitleValue('');
  };

  // URL Import handler
  const handleImportUrl = async () => {
    if (!urlInput.trim()) return;
    
    setImportingUrl(true);
    try {
      // Check for duplicate before importing
      const dupeCheck = await recipesApi.checkRecipeExists(urlInput.trim());
      if (dupeCheck.data.exists) {
        setMessages(prev => [
          ...prev,
          { role: 'user', content: `Import recipe from: ${urlInput.trim()}` },
          { role: 'assistant', content: `You already have this recipe${dupeCheck.data.title ? ` — **${dupeCheck.data.title}**` : ''}! You can find it in your Recipe Box.\n\n[VIEW_RECIPE:${dupeCheck.data.recipe_id}]` }
        ]);
        setShowUrlModal(false);
        setUrlInput('');
        setImportingUrl(false);
        return;
      }

      const response = await ai.importRecipeFromURL(urlInput.trim());
      
      if (response.data.success) {
        // Store the source image if available (keyed by source URL)
        const sourceUrl = response.data.source_url;
        if (response.data.source_image) {
          setImportedSourceImages(prev => ({
            ...prev,
            [sourceUrl]: response.data.source_image
          }));
        }
        
        // Add the imported recipe to the chat with a special marker for the source URL
        const importMessage = `I found this recipe from ${response.data.source_url}:\n\n${response.data.recipe_text}\n\n[SOURCE_URL:${response.data.source_url}]`;
        
        // Update messages - the useEffect will auto-save the chat
        setMessages(prev => [
          ...prev,
          { role: 'user', content: `Import recipe from: ${urlInput.trim()}` },
          { role: 'assistant', content: importMessage }
        ]);
        
        setShowUrlModal(false);
        setUrlInput('');
      }
    } catch (error) {
      console.error('Failed to import recipe:', error);
      alert(error.response?.data?.detail || 'Failed to import recipe from URL. Please check the URL and try again.');
    } finally {
      setImportingUrl(false);
    }
  };

  // Load chat from API or start fresh
  // Use a ref to track if we should skip the next fetch (avoids race condition with state updates)
  const skipNextFetchRef = useRef(false);
  // Track if we're currently loading a chat (to avoid updating timestamp on load)
  const isLoadingChatRef = useRef(false);
  // Track previous message count to detect new messages vs just loading
  const prevMessageCountRef = useRef(1);
  
  useEffect(() => {
    const loadChat = async () => {
      console.log('[ChatRecipeCreator] loadChat called, currentChatId:', currentChatId, 'recipeId:', recipeId, 'skipNextFetch:', skipNextFetchRef.current);
      
      // Skip fetch if we just created this chat (to avoid race condition)
      if (skipNextFetchRef.current) {
        console.log('[ChatRecipeCreator] Skipping fetch (just created chat)');
        skipNextFetchRef.current = false;
        setChatLoading(false);
        return;
      }
      
      // For recipe-specific chats, load by recipe_id
      if (recipeId && !currentChatId) {
        try {
          isLoadingChatRef.current = true;
          console.log('[ChatRecipeCreator] Fetching chat for recipe:', recipeId);
          const response = await chatsApi.getByRecipeId(recipeId);
          if (response.data) {
            console.log('[ChatRecipeCreator] Found existing recipe chat:', response.data.chat_id);
            setCurrentChatId(response.data.chat_id);
            if (response.data.messages) {
              prevMessageCountRef.current = response.data.messages.length;
              setMessages(response.data.messages);
            }
            if (response.data.saved_recipes) {
              setSavedRecipes(response.data.saved_recipes);
            }
            if (response.data.title) {
              setChatTitle(response.data.title);
            }
          } else {
            console.log('[ChatRecipeCreator] No existing chat for recipe, starting fresh');
            // No existing chat - keep default welcome message
          }
        } catch (error) {
          console.error('Failed to load recipe chat:', error);
        } finally {
          setTimeout(() => {
            isLoadingChatRef.current = false;
          }, 100);
        }
        setChatLoading(false);
        return;
      }
      
      // For general chats (no recipeId), load by currentChatId from localStorage
      if (currentChatId) {
        try {
          // Mark that we're loading - prevents timestamp update on load
          isLoadingChatRef.current = true;
          console.log('[ChatRecipeCreator] Fetching chat from API...');
          const response = await chatsApi.getById(currentChatId);
          if (response.data) {
            console.log('[ChatRecipeCreator] Got chat data, messages count:', response.data.messages?.length);
            if (response.data.messages) {
              // Update the previous message count to match loaded messages
              // This prevents the save effect from thinking new messages were added
              prevMessageCountRef.current = response.data.messages.length;
              setMessages(response.data.messages);
            }
            // Load saved recipes mapping
            if (response.data.saved_recipes) {
              setSavedRecipes(response.data.saved_recipes);
            }
            // Load chat title
            if (response.data.title) {
              setChatTitle(response.data.title);
            }
          }
        } catch (error) {
          console.error('Failed to load chat:', error);
          // Chat not found, start fresh
          localStorage.removeItem('currentChatId');
          setCurrentChatId(null);
        } finally {
          // Reset loading flag after a short delay to allow state to settle
          setTimeout(() => {
            isLoadingChatRef.current = false;
          }, 100);
        }
      }
      setChatLoading(false);
    };
    loadChat();
  }, [currentChatId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Save chat to API when messages change (debounced)
  useEffect(() => {
    if (chatLoading || messages.length <= 1) return;
    
    // Check if we're just loading a chat (not adding new messages)
    // Only update timestamp when new messages are added
    const hasNewMessages = messages.length > prevMessageCountRef.current;
    const shouldUpdateTimestamp = hasNewMessages && !isLoadingChatRef.current;
    
    // Update the previous message count for next comparison
    prevMessageCountRef.current = messages.length;
    
    const saveTimeout = setTimeout(async () => {
      try {
        if (currentChatId) {
          // Update existing chat - only update timestamp if new messages were added
          console.log('[ChatRecipeCreator] Updating existing chat:', currentChatId, 'updateTimestamp:', shouldUpdateTimestamp);
          await chatsApi.update(currentChatId, { messages, update_timestamp: shouldUpdateTimestamp });
        } else {
          // Create new chat - set flag to skip re-fetch using ref (synchronous)
          console.log('[ChatRecipeCreator] Creating new chat...', recipeId ? `for recipe: ${recipeId}` : '(general)');
          const response = await chatsApi.create({ 
            messages,
            recipe_id: recipeId || null  // Anchor to recipe if provided
          });
          if (response.data && response.data.chat_id) {
            console.log('[ChatRecipeCreator] New chat created:', response.data.chat_id, '- setting skipNextFetch');
            skipNextFetchRef.current = true;
            setCurrentChatId(response.data.chat_id);
            // Only save to localStorage for general chats (not recipe-specific)
            if (!recipeId) {
              localStorage.setItem('currentChatId', response.data.chat_id);
            }
          }
        }
      } catch (error) {
        console.error('Failed to save chat:', error);
      }
    }, 1000); // Debounce 1 second
    
    return () => clearTimeout(saveTimeout);
  }, [messages, currentChatId, chatLoading, recipeId]);

  // Function to start a new chat
  const handleStartNewChat = () => {
    if (messages.length <= 1 || window.confirm('Start a new chat? Your current conversation will be saved.')) {
      // For recipe-specific chats, don't touch localStorage
      if (!recipeId) {
        localStorage.removeItem('currentChatId');
      }
      setCurrentChatId(null);
      setChatTitle(recipeTitle ? `Chat: ${recipeTitle}` : null);
      setMessages([{
        role: 'assistant',
        content: getWelcomeMessage(),
      }]);
      setSavedRecipes({});
    }
  };

  // Helper to detect if text contains a recipe URL
  const extractRecipeUrl = (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/gi;
    const matches = text.match(urlRegex);
    if (matches) {
      // Check if any URL looks like a recipe site
      for (const url of matches) {
        const lowerUrl = url.toLowerCase();
        if (lowerUrl.includes('recipe') || 
            lowerUrl.includes('allrecipes') ||
            lowerUrl.includes('foodnetwork') ||
            lowerUrl.includes('epicurious') ||
            lowerUrl.includes('bonappetit') ||
            lowerUrl.includes('seriouseats') ||
            lowerUrl.includes('food52') ||
            lowerUrl.includes('tasty') ||
            lowerUrl.includes('delish') ||
            lowerUrl.includes('simplyrecipes') ||
            lowerUrl.includes('cooking') ||
            lowerUrl.includes('yummly') ||
            lowerUrl.includes('gimmesomeoven') ||
            lowerUrl.includes('budgetbytes') ||
            lowerUrl.includes('minimalistbaker') ||
            lowerUrl.includes('skinnytaste') ||
            lowerUrl.includes('halfbakedharvest') ||
            lowerUrl.includes('pinchofyum') ||
            lowerUrl.includes('loveandlemons') ||
            lowerUrl.includes('smittenkitchen') ||
            lowerUrl.includes('thekitchn') ||
            lowerUrl.includes('marthastewart') ||
            lowerUrl.includes('thepioneerwoman') ||
            lowerUrl.includes('spendwithpennies') ||
            lowerUrl.includes('damndelicious') ||
            lowerUrl.includes('tiktok.com') ||
            lowerUrl.includes('instagram.com')) {
          return url;
        }
      }
      // If URL doesn't match known recipe sites, still try if user explicitly mentions recipe/import
      if (text.toLowerCase().includes('recipe') || text.toLowerCase().includes('import')) {
        return matches[0];
      }
    }
    return null;
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    
    // Check if user pasted a URL - auto-import it
    const recipeUrl = extractRecipeUrl(input.trim());
    if (recipeUrl) {
      // User pasted a URL - import it
      setUrlInput(recipeUrl);
      await handleImportUrlDirect(recipeUrl);
      setInput('');
      return;
    }
    
    await handleSendMessage(null, input.trim());
    setInput('');
  };
  
  // Direct URL import (called when URL is detected in chat)
  const handleImportUrlDirect = async (url) => {
    setLoading(true);
    setMessages(prev => [...prev, { role: 'user', content: `Import recipe from: ${url}` }]);
    
    try {
      // Check for duplicate before importing
      const dupeCheck = await recipesApi.checkRecipeExists(url);
      if (dupeCheck.data.exists) {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: `You already have this recipe${dupeCheck.data.title ? ` — **${dupeCheck.data.title}**` : ''}! You can find it in your Recipe Box.\n\n[VIEW_RECIPE:${dupeCheck.data.recipe_id}]` 
        }]);
        setLoading(false);
        return;
      }

      const response = await ai.importRecipeFromURL(url);
      
      if (response.data.success) {
        // Store the source image if available (keyed by source URL)
        const sourceUrl = response.data.source_url;
        if (response.data.source_image) {
          setImportedSourceImages(prev => ({
            ...prev,
            [sourceUrl]: response.data.source_image
          }));
        }
        
        const importMessage = `I found this recipe from ${response.data.source_url}:\n\n${response.data.recipe_text}\n\n[SOURCE_URL:${response.data.source_url}]`;
        setMessages(prev => [...prev, { role: 'assistant', content: importMessage }]);
      } else {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: 'Sorry, I couldn\'t import that recipe. Please check the URL and try again.' 
        }]);
      }
    } catch (error) {
      console.error('Failed to import recipe:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `Sorry, I couldn't import that recipe: ${error.response?.data?.detail || 'Please check the URL and try again.'}` 
      }]);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSendMessage = async (e, messageText) => {
    if (e) e.preventDefault();
    if (!messageText || loading) return;

    // Track message sent
    trackChatMessageSent(currentChatId, messageText.length);

    const userMessage = { role: 'user', content: messageText };
    setMessages((prev) => [...prev, userMessage]);
    setLoading(true);

    try {
      // Use shared utility for chat with food preferences
      const result = await sendChatMessage([...messages, userMessage], foodPreferences, measurementSystem);
      
      if (result.success) {
        setMessages((prev) => [...prev, { role: 'assistant', content: result.message }]);
      } else {
        // Error occurred but we still have a message (error message)
        setMessages((prev) => [...prev, { role: 'assistant', content: result.message }]);
      }
    } catch (error) {
      console.error('Failed to chat:', error);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Save a specific recipe by name and navigate to detail
  const handleSaveRecipe = async (recipeName, messageContent) => {
    if (loading || savingRecipeName !== null) return;
    
    console.log('[handleSaveRecipe] Starting save for:', recipeName);
    console.log('[handleSaveRecipe] Current savedRecipes:', savedRecipes);
    setSavingRecipeName(recipeName);
    
    try {
      // Check if this is an imported recipe with a source URL
      const sourceUrlMatch = messageContent.match(/\[SOURCE_URL:([^\]]+)\]/);
      const sourceUrl = sourceUrlMatch ? sourceUrlMatch[1] : null;
      
      // Remove the SOURCE_URL marker from content before extraction
      const cleanContent = messageContent.replace(/\[SOURCE_URL:[^\]]+\]/, '').trim();
      
      // If this is an imported recipe, extract any modifications made in chat
      let chatModifications = [];
      if (sourceUrl) {
        // Find the SPECIFIC import message for THIS recipe (the one we're saving from)
        // We need to find the import message that contains this sourceUrl
        const importMsgIndex = messages.findIndex(m => 
          m.role === 'assistant' && m.content.includes(`[SOURCE_URL:${sourceUrl}]`)
        );
        
        if (importMsgIndex >= 0) {
          // Find the NEXT import message (if any) to know where modifications for THIS recipe end
          const nextImportIndex = messages.findIndex((m, idx) => 
            idx > importMsgIndex && m.role === 'assistant' && m.content.includes('[SOURCE_URL:')
          );
          
          // Get only user messages between THIS import and the next import (or end of messages)
          const endIndex = nextImportIndex > 0 ? nextImportIndex : messages.length;
          const modificationMessages = messages
            .slice(importMsgIndex + 1, endIndex)
            .filter(m => m.role === 'user')
            .map(m => m.content)
            .filter(content => content.trim().length > 0);
          
          if (modificationMessages.length > 0) {
            // Use dedicated AI summarization endpoint for clean descriptions
            try {
              const summarizeResponse = await ai.summarizeText(
                modificationMessages.map((m, i) => `${i + 1}. "${m}"`).join('\n'),
                `Summarize these recipe modification requests into clean, concise descriptions of what was changed. Fix any typos. Return ONLY a JSON array of short summary strings (e.g., "Added powdered sugar topping"). Return as JSON array like: ["Added X", "Reduced Y by half"]`
              );
              
              const responseText = summarizeResponse.data.summary || '';
              const jsonMatch = responseText.match(/\[[\s\S]*\]/);
              if (jsonMatch) {
                chatModifications = JSON.parse(jsonMatch[0]);
              }
            } catch (summarizeError) {
              console.error('Failed to summarize modifications:', summarizeError);
              // Don't include raw messages as fallback - better to have no modifications than messy ones
            }
          }
        }
      }
      
      // Create extraction context with just the relevant message
      const extractionMessages = [
        { role: 'assistant', content: cleanContent }
      ];
      
      const result = await extractSingleRecipe(extractionMessages, recipeName, foodPreferences, measurementSystem);
      console.log('[handleSaveRecipe] Extract result:', result.success, result.recipe?.title);
      
      if (result.success && result.recipe) {
        // Pass source URL, modifications, and source image if this was an imported recipe
        const generationMethod = sourceUrl ? 'url_import' : 'chat';
        const sourceImage = sourceUrl ? importedSourceImages[sourceUrl] : null;
        console.log('[handleSaveRecipe] Calling saveRecipe with generationMethod:', generationMethod, 'sourceImage:', sourceImage ? 'present' : 'none');
        const saveResult = await saveRecipe(result.recipe, generationMethod, sourceUrl, chatModifications, sourceImage);
        console.log('[handleSaveRecipe] Save result:', saveResult);
        console.log('[handleSaveRecipe] New recipe ID:', saveResult.recipeId);
        
        if (saveResult.success && saveResult.recipeId) {
          // Mark this recipe as saved with its ID
          const newSavedRecipes = { ...savedRecipes, [recipeName]: saveResult.recipeId };
          setSavedRecipes(newSavedRecipes);
          console.log('[handleSaveRecipe] Updated savedRecipes:', newSavedRecipes);
          
          // Update the chat in the backend with saved_recipes
          if (currentChatId) {
            try {
              await chatsApi.update(currentChatId, { saved_recipes: { [recipeName]: saveResult.recipeId } });
            } catch (err) {
              console.error('Failed to update saved_recipes in chat:', err);
            }
          }
          
          // Track recipe created from chat
          trackRecipeCreatedChat(saveResult.recipeId, saveResult.title || recipeName);
          
          // Navigate to recipe detail - use the NEW recipe ID from save result
          // Adding timestamp to force fresh load and prevent any caching issues
          const newRecipeUrl = `/recipe/${saveResult.recipeId}?fromChat=true&t=${Date.now()}`;
          console.log('[handleSaveRecipe] Navigating to:', newRecipeUrl);
          navigate(newRecipeUrl);
        } else {
          console.error('[handleSaveRecipe] Save failed:', saveResult);
          alert('Failed to save recipe. Please try again.');
        }
      } else {
        console.error('[handleSaveRecipe] Extract failed:', result);
        alert(`Could not extract "${recipeName}". Try asking me to show just that recipe.`);
      }
    } catch (error) {
      console.error('Failed to save recipe:', error);
      alert('Error saving recipe. Please try again.');
    } finally {
      setSavingRecipeName(null);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Validate all files
    const validFiles = files.filter(file => 
      ['image/jpeg', 'image/png', 'image/webp'].includes(file.type)
    );
    
    if (validFiles.length === 0) {
      alert('Please upload JPEG, PNG, or WEBP images');
      return;
    }

    if (validFiles.length !== files.length) {
      alert(`${files.length - validFiles.length} file(s) were skipped (unsupported format)`);
    }

    // Process files and add to pending images
    const processedImages = [];
    let processed = 0;
    
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target.result;
        const base64 = dataUrl.split(',')[1];
        processedImages.push({
          preview: dataUrl,
          base64
        });
        processed++;
        
        // Once all files are processed, show the type selection modal
        if (processed === validFiles.length) {
          setPendingImages(prev => [...prev, ...processedImages]);
          setShowImageTypeModal(true);
          setSelectedImageType(null); // Reset selection
        }
      };
      reader.readAsDataURL(file);
    });

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemovePendingImage = (index) => {
    setPendingImages(prev => prev.filter((_, i) => i !== index));
    // If no images left, close the modal
    if (pendingImages.length <= 1) {
      setShowImageTypeModal(false);
    }
  };

  const handleClearPendingImages = () => {
    setPendingImages([]);
    setShowImageTypeModal(false);
  };

  const handleAnalyzeImages = async () => {
    if (pendingImages.length === 0 || loading || !selectedImageType) return;

    setLoading(true);
    setShowImageTypeModal(false);
    const imageCount = pendingImages.length;
    
    // Add user message showing they uploaded images
    setMessages((prev) => [...prev, { 
      role: 'user', 
      content: `📷 *Uploaded ${imageCount} image${imageCount > 1 ? 's' : ''} for analysis*` 
    }]);

    // Clear pending images
    const imagesToAnalyze = [...pendingImages];
    setPendingImages([]);

    try {
      // Use shared utility for image analysis with selected type and food preferences
      const result = await analyzeImages(imagesToAnalyze, selectedImageType, foodPreferences, measurementSystem);

      if (result.success && result.message) {
        setMessages((prev) => [...prev, { 
          role: 'assistant', 
          content: result.message 
        }]);
      } else {
        setMessages((prev) => [...prev, { 
          role: 'assistant', 
          content: result.message || "I couldn't extract a recipe from those images. Please try clearer images or describe the recipe to me!" 
        }]);
      }
    } catch (error) {
      console.error('Failed to analyze images:', error);
      setMessages((prev) => [...prev, { 
        role: 'assistant', 
        content: "Sorry, I had trouble analyzing those images. Please try again or describe the recipe to me!" 
      }]);
    } finally {
      setLoading(false);
      setSelectedImageType(null);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="bg-white shadow-sm sticky top-0 z-50 border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <button
                onClick={onBack}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
              >
                <ArrowLeft size={20} />
              </button>
              {isEditingTitle ? (
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <input
                    ref={titleInputRef}
                    type="text"
                    value={editingTitleValue}
                    onChange={(e) => setEditingTitleValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveTitle();
                      if (e.key === 'Escape') handleCancelEditTitle();
                    }}
                    className="flex-1 min-w-0 text-xl font-bold text-slate-800 px-2 py-1 border border-amber-300 rounded focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                  <button
                    onClick={handleSaveTitle}
                    className="p-1.5 text-green-600 hover:bg-green-50 rounded flex-shrink-0"
                  >
                    <Check size={18} />
                  </button>
                  <button
                    onClick={handleCancelEditTitle}
                    className="p-1.5 text-gray-400 hover:bg-gray-100 rounded flex-shrink-0"
                  >
                    <X size={18} />
                  </button>
                </div>
              ) : (
                <h1 
                  onClick={currentChatId ? handleStartEditTitle : undefined}
                  className={`text-2xl font-bold text-slate-800 truncate max-w-[250px] sm:max-w-none ${currentChatId ? 'cursor-pointer hover:text-amber-600' : ''}`}
                  title={currentChatId ? 'Click to edit title' : undefined}
                >
                  {chatTitle || 'New Recipe Chat'}
                </h1>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col relative" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}>
        {/* Messages */}
        <div className="flex-1 overflow-y-auto mb-4 space-y-4">
          {messages.flatMap((msg, msgIdx) => {
            // For assistant messages with multiple recipes, split into separate boxes
            if (msg.role === 'assistant' && messageHasRecipe(msg.content)) {
              const recipeNames = extractAllRecipeNames(msg.content);
              
              if (recipeNames.length > 1) {
                const recipes = splitIntoRecipes(msg.content);
                
                // Multiple recipes - render each in its own box with image
                return recipes.map((recipe, recipeIdx) => (
                  <div
                    key={`${msgIdx}-${recipeIdx}`}
                    className="flex justify-start"
                  >
                    <div className="max-w-[85%] rounded-lg bg-white text-gray-900 shadow-sm overflow-hidden">
                      <div className="px-4 py-3">
                        <div className="leading-snug recipe-content">
                          <ReactMarkdown components={recipeMarkdownComponents}>
                            {recipe.content}
                          </ReactMarkdown>
                          {/* AI nutrition disclaimer */}
                          {recipe.content && /nutrition/i.test(recipe.content) && (
                            <p className="text-xs text-gray-400 mt-2 italic" data-testid="chat-nutrition-disclaimer">
                              Nutritional values are AI-estimated. For verified data, consult <a href="https://fdc.nal.usda.gov" target="_blank" rel="noopener noreferrer" className="underline">USDA FoodData Central</a>.
                            </p>
                          )}
                          {/* Add to Recipe Box button for this specific recipe */}
                          {recipe.name && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              {savedRecipes[recipe.name] ? (
                                <div className="flex items-center gap-2 text-green-600 text-sm font-medium">
                                  <Check size={16} />
                                  <span>{recipe.name} saved</span>
                                  <span className="text-gray-400">·</span>
                                  <button
                                    onClick={() => navigate(`/recipe/${savedRecipes[recipe.name]}?fromChat=true`)}
                                    className="text-blue-600 hover:text-blue-700 hover:underline"
                                  >
                                    View Recipe →
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => handleSaveRecipe(recipe.name, recipe.content)}
                                  disabled={loading || savingRecipeName !== null}
                                  className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                                >
                                  {savingRecipeName === recipe.name ? (
                                    <>
                                      <Loader size={16} className="animate-spin" />
                                      {loadingPhrase}
                                    </>
                                  ) : (
                                    <>📥 Add {recipe.name}</>
                                  )}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ));
              }
            }
            
            // Single message (user or single-recipe assistant) - render normally
            // Check if this is a single-recipe assistant message first
            const singleRecipeNames = msg.role === 'assistant' ? extractAllRecipeNames(msg.content) : [];
            const hasSingleRecipe = msg.role === 'assistant' && messageHasRecipe(msg.content) && singleRecipeNames.length === 1;
            const singleRecipeName = hasSingleRecipe ? singleRecipeNames[0] : null;
            
            // For single recipe messages, use a different layout
            if (hasSingleRecipe && singleRecipeName) {
              return [(
                <div
                  key={msgIdx}
                  className="flex justify-start"
                >
                  <div className="max-w-[85%] rounded-lg bg-white text-gray-900 shadow-sm overflow-hidden">
                    <div className="px-4 py-3">
                      <div className="leading-snug recipe-content">
                        <ReactMarkdown components={chatMarkdownComponents}>
                          {msg.content}
                        </ReactMarkdown>
                        {/* AI nutrition disclaimer */}
                        {msg.content && /nutrition/i.test(msg.content) && (
                          <p className="text-xs text-gray-400 mt-2 italic" data-testid="chat-nutrition-disclaimer-single">
                            Nutritional values are AI-estimated. For verified data, consult <a href="https://fdc.nal.usda.gov" target="_blank" rel="noopener noreferrer" className="underline">USDA FoodData Central</a>.
                          </p>
                        )}
                        {/* Add to Recipe Box button */}
                        <div className="mt-3 pt-3 border-t border-gray-200 relative">
                          {savedRecipes[singleRecipeName] ? (
                            <div className="flex items-center gap-2 text-green-600 text-sm font-medium">
                              <Check size={16} />
                              <span>{singleRecipeName} saved</span>
                              <span className="text-gray-400">·</span>
                              <button
                                onClick={() => navigate(`/recipe/${savedRecipes[singleRecipeName]}?fromChat=true`)}
                                className="text-blue-600 hover:text-blue-700 hover:underline"
                              >
                                View Recipe →
                              </button>
                            </div>
                          ) : (
                            <div className="relative">
                              <button
                                onClick={() => {
                                  handleSaveRecipe(singleRecipeName, msg.content);
                                  if (showAddToBoxTip) {
                                    setShowAddToBoxTip(false);
                                    localStorage.setItem('hasSeenAddToBoxTip', 'true');
                                  }
                                }}
                                disabled={loading || savingRecipeName !== null}
                                className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                              >
                                {savingRecipeName === singleRecipeName ? (
                                  <>
                                    <Loader size={16} className="animate-spin" />
                                    {loadingPhrase}
                                  </>
                                ) : (
                                  <>📥 Add to Recipe Box</>
                                )}
                              </button>
                              {/* First-time tip */}
                              {showAddToBoxTip && !savedRecipes[singleRecipeName] && (
                                <div className="absolute bottom-full left-0 mb-2 w-56 bg-white rounded-lg shadow-xl border border-green-200 p-3 z-50 animate-fade-in">
                                  <button 
                                    onClick={() => {
                                      setShowAddToBoxTip(false);
                                      localStorage.setItem('hasSeenAddToBoxTip', 'true');
                                    }}
                                    className="absolute top-1 right-1 text-gray-400 hover:text-gray-600 text-sm"
                                  >
                                    ✕
                                  </button>
                                  <div className="flex items-start gap-2">
                                    <span className="text-xl">📥</span>
                                    <div>
                                      <p className="text-sm font-medium text-gray-800 mb-1">Save Your Recipe!</p>
                                      <p className="text-xs text-gray-600">
                                        Tap here to save this recipe to your Recipe Box for easy access anytime.
                                      </p>
                                    </div>
                                  </div>
                                  <div className="absolute -bottom-2 left-4 w-3 h-3 bg-white border-r border-b border-green-200 transform rotate-45"></div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )];
            }
            
            // Regular message (user or non-recipe assistant)
            return [(
            <div
              key={msgIdx}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] px-4 py-3 rounded-lg ${
                  msg.role === 'user'
                    ? 'bg-amber-600 text-white'
                    : 'bg-white text-gray-900 shadow-sm'
                }`}
              >
                <div className="leading-snug recipe-content">
                  {(() => {
                    // Parse content for [CHOICE:action:label] markers
                    const choiceRegex = /\[CHOICE:([^:]+):([^\]]+)\]/g;
                    const choices = [];
                    let match;
                    let contentWithoutChoices = msg.content;
                    
                    while ((match = choiceRegex.exec(msg.content)) !== null) {
                      choices.push({ action: match[1], label: match[2] });
                    }
                    
                    // Remove choice markers from content
                    contentWithoutChoices = contentWithoutChoices.replace(choiceRegex, '').trim();
                    
                    // Remove SOURCE_URL markers from rendered content
                    contentWithoutChoices = contentWithoutChoices.replace(/\[SOURCE_URL:[^\]]+\]/g, '').trim();
                    
                    // Extract VIEW_RECIPE links
                    const viewRecipeMatch = contentWithoutChoices.match(/\[VIEW_RECIPE:([^\]]+)\]/);
                    const viewRecipeId = viewRecipeMatch ? viewRecipeMatch[1] : null;
                    contentWithoutChoices = contentWithoutChoices.replace(/\[VIEW_RECIPE:[^\]]+\]/g, '').trim();
                    
                    // Group choices into pairs (yes/no for each warning)
                    const choiceGroups = [];
                    for (let i = 0; i < choices.length; i += 2) {
                      if (choices[i + 1]) {
                        choiceGroups.push([choices[i], choices[i + 1]]);
                      } else {
                        choiceGroups.push([choices[i]]);
                      }
                    }
                    
                    const isLastMessage = msgIdx === messages.length - 1;
                    const hasChoices = choices.length > 0 && isLastMessage;
                    
                    return (
                      <>
                        <ReactMarkdown components={chatMarkdownComponents}>
                          {contentWithoutChoices}
                        </ReactMarkdown>
                        {viewRecipeId && (
                          <button
                            onClick={() => navigate(`/recipe/${viewRecipeId}`)}
                            className="mt-3 px-4 py-2 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 transition-colors text-sm"
                            data-testid="chat-view-existing-recipe-btn"
                          >
                            View Existing Recipe
                          </button>
                        )}
                        {hasChoices && (
                          <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                            {choiceGroups.map((group, groupIdx) => (
                              <div key={groupIdx} className="flex flex-wrap gap-2">
                                {group.map((choice, choiceIdx) => {
                                  const isSelected = pendingChoices[groupIdx] === choice.label;
                                  const isPositive = !choice.action.includes('keep') && !choice.action.includes('no');
                                  return (
                                    <button
                                      key={choiceIdx}
                                      onClick={() => {
                                        setPendingChoices(prev => ({
                                          ...prev,
                                          [groupIdx]: choice.label
                                        }));
                                      }}
                                      disabled={loading}
                                      className={`px-3 py-1.5 text-sm rounded-lg transition-colors border-2 ${
                                        isSelected
                                          ? isPositive
                                            ? 'bg-amber-600 text-white border-amber-600'
                                            : 'bg-gray-600 text-white border-gray-600'
                                          : isPositive
                                            ? 'bg-amber-50 text-amber-700 border-amber-200 hover:border-amber-400'
                                            : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-gray-400'
                                      }`}
                                    >
                                      {choice.label}
                                    </button>
                                  );
                                })}
                              </div>
                            ))}
                            {/* Continue button - only show when all groups have a selection */}
                            {Object.keys(pendingChoices).length === choiceGroups.length && (
                              <button
                                onClick={() => {
                                  const selectedLabels = Object.values(pendingChoices);
                                  const combinedMessage = selectedLabels.join('. ');
                                  setPendingChoices({});
                                  handleSendMessage(null, combinedMessage);
                                }}
                                disabled={loading}
                                className="w-full mt-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-medium transition-colors"
                              >
                                Continue →
                              </button>
                            )}
                          </div>
                        )}
                        {/* Add to Recipe Box button for single-recipe messages */}
                        {hasSingleRecipe && (() => {
                          const recipeName = singleRecipeNames[0];
                          const savedRecipeId = savedRecipes[recipeName];
                          const isSaving = savingRecipeName === recipeName;
                          
                          return (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              {savedRecipeId ? (
                                <div className="flex items-center gap-2 text-green-600 text-sm font-medium">
                                  <Check size={16} />
                                  <span>{recipeName} saved</span>
                                  <span className="text-gray-400">·</span>
                                  <button
                                    onClick={() => navigate(`/recipe/${savedRecipeId}?fromChat=true`)}
                                    className="text-blue-600 hover:text-blue-700 hover:underline"
                                  >
                                    View Recipe →
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => handleSaveRecipe(recipeName, msg.content)}
                                  disabled={loading || savingRecipeName !== null}
                                  className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                                >
                                  {isSaving ? (
                                    <>
                                      <Loader size={16} className="animate-spin" />
                                      {loadingPhrase}
                                    </>
                                  ) : (
                                    <>📥 Add {recipeName}</>
                                  )}
                                </button>
                              )}
                            </div>
                          );
                        })()}
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          )];
          })}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-white px-4 py-3 rounded-lg shadow-sm flex items-center gap-2">
                <Loader className="animate-spin" size={20} />
                <span>{loadingPhrase}</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area - Fixed at bottom with Food Preferences above */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-4">
          {/* Food Preferences - above input, expands upward */}
          <div className="mb-4">
            <FoodPreferences 
              onPreferencesChange={setFoodPreferences}
              compact={true}
              defaultExpanded={preferencesExpanded}
              expandUp={true}
              onExpand={() => {
                // Scroll to bottom when preferences expand so user can see the expanded content
                setTimeout(() => {
                  window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
                }, 100);
              }}
              onCollapse={() => {
                setPreferencesExpanded(false);
                localStorage.setItem('foodPrefsClosedV2', 'true');
              }}
            />
          </div>

          {/* Show pending images count badge when modal is closed */}
            {pendingImages.length > 0 && !showImageTypeModal && (
              <button
                onClick={() => setShowImageTypeModal(true)}
                className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg w-full text-left hover:bg-amber-100 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-amber-700">
                    📷 {pendingImages.length} image{pendingImages.length !== 1 ? 's' : ''} ready to analyze
                  </span>
                  <span className="text-xs text-amber-600 font-medium">Click to continue →</span>
                </div>
              </button>
            )}

            {/* Mobile input layout - stacked buttons like DraggableChat */}
            <form onSubmit={(e) => { e.preventDefault(); handleSend(); }} className="sm:hidden flex gap-2 items-end">
              <div className="flex flex-col gap-1">
                <button
                  type="button"
                  onClick={() => setShowUrlModal(true)}
                  className="flex items-center justify-center p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors h-9 w-9"
                  title="Import recipe from URL"
                  disabled={loading}
                >
                  <Link size={16} className="text-gray-600" />
                </button>
                <label className="flex items-center justify-center p-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors h-9 w-9" title="Upload image(s)">
                  <Image size={16} className="text-gray-600" />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={loading}
                    multiple
                  />
                </label>
              </div>
              <textarea
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder={pendingImages.length > 0 ? "Describe images..." : (messages.length > 1 ? "Any changes?" : "Describe what you want to cook...")}
                data-testid="chat-input-mobile"
                className="flex-1 px-3 py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none overflow-hidden"
                style={{ fontSize: '16px', minHeight: '76px', maxHeight: '150px' }}
                disabled={loading}
                rows={2}
              />
              <button
                type="submit"
                disabled={!input.trim() || loading}
                data-testid="send-message-button-mobile"
                className="p-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors h-[76px] w-10"
              >
                <Send size={18} />
              </button>
            </form>

            {/* Desktop input layout - original horizontal */}
            <form onSubmit={(e) => { e.preventDefault(); handleSend(); }} className="hidden sm:flex gap-3 items-end">
              <textarea
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  // Auto-resize textarea
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
                }}
                onKeyDown={(e) => {
                  // Submit on Enter (without Shift)
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder={pendingImages.length > 0 ? "Or describe what's in the images..." : (messages.length > 1 ? "How does that sound? Any changes?" : "Describe what you want to cook...")}
                data-testid="chat-input"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none overflow-hidden"
                disabled={loading}
                autoFocus
                rows={1}
                style={{ minHeight: '48px', maxHeight: '150px' }}
              />
              <button
                type="button"
                onClick={() => setShowUrlModal(true)}
                className="flex items-center justify-center p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors h-12"
                title="Import recipe from URL"
                disabled={loading}
              >
                <Link size={20} className="text-gray-600" />
              </button>
              <label className="flex items-center justify-center p-3 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors h-12" title="Upload image(s)">
                <Image size={20} className="text-gray-600" />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleImageUpload}
                  className="hidden"
                  disabled={loading}
                  multiple
                />
              </label>
              <button
                type="submit"
                disabled={!input.trim() || loading}
                data-testid="send-message-button"
                className="px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2 h-12"
              >
                <Send size={20} />
              </button>
            </form>
        </div>
      </main>

      {/* URL Import Modal */}
      {showUrlModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Import Recipe from URL</h2>
                <button
                  onClick={() => {
                    setShowUrlModal(false);
                    setUrlInput('');
                  }}
                  className="p-1 hover:bg-gray-100 rounded-full"
                >
                  <X size={20} />
                </button>
              </div>
              
              <p className="text-gray-600 mb-4">
                Paste a link to a recipe from any website and we&apos;ll import it for you.
              </p>
              
              <input
                type="url"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleImportUrl()}
                placeholder="https://example.com/recipe..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent mb-4"
                autoFocus
                disabled={importingUrl}
              />
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowUrlModal(false);
                    setUrlInput('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={importingUrl}
                >
                  Cancel
                </button>
                <button
                  onClick={handleImportUrl}
                  disabled={!urlInput.trim() || importingUrl}
                  className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {importingUrl ? (
                    <>
                      <Loader size={16} className="animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Link size={16} />
                      Import
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Type Selection Modal */}
      {showImageTypeModal && pendingImages.length > 0 && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Upload Image</h2>
                <button
                  onClick={handleClearPendingImages}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Image Type Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  What type of image are you uploading?
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setSelectedImageType('recipe_card')}
                    className={`p-4 border-2 rounded-lg text-left transition-colors ${
                      selectedImageType === 'recipe_card'
                        ? 'border-amber-600 bg-amber-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="font-medium text-gray-900 mb-1">Recipe Card/Screenshot</div>
                    <div className="text-sm text-gray-600">Extract recipe from text in image</div>
                  </button>
                  <button
                    onClick={() => setSelectedImageType('dish_photo')}
                    className={`p-4 border-2 rounded-lg text-left transition-colors ${
                      selectedImageType === 'dish_photo'
                        ? 'border-amber-600 bg-amber-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="font-medium text-gray-900 mb-1">Dish Photo</div>
                    <div className="text-sm text-gray-600">AI creates recipe from finished dish</div>
                  </button>
                </div>
              </div>

              {/* Image Preview Grid */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    {pendingImages.length} image{pendingImages.length !== 1 ? 's' : ''} selected
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {pendingImages.map((img, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={img.preview}
                        alt={`Upload ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg bg-gray-100"
                      />
                      <span className="absolute top-1 left-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded">
                        {index + 1}
                      </span>
                      <button
                        onClick={() => handleRemovePendingImage(index)}
                        className="absolute -top-2 -right-2 p-1 bg-white rounded-full shadow hover:bg-gray-100 transition-colors"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                  {/* Add More Button */}
                  <label className="h-24 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-amber-600 hover:bg-amber-50 transition-colors">
                    <Plus size={24} className="text-gray-400 mb-1" />
                    <span className="text-xs text-gray-500">Add More</span>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleImageUpload}
                      className="hidden"
                      multiple
                    />
                  </label>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleClearPendingImages}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAnalyzeImages}
                  disabled={loading || !selectedImageType}
                  className="flex-1 px-4 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader className="animate-spin" size={16} />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Image size={16} />
                      Analyze {pendingImages.length > 1 ? `${pendingImages.length} Images` : 'Image'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatRecipeCreator;